/**
 * Global Store
 *
 * Main Zustand store combining all slices for the Spellion application.
 * Uses Zustand for simple, efficient state management.
 *
 * Features:
 * - User state: user, token, isAuthenticated, subscriptionType
 * - Search state: query, results, isLoading, quota
 * - Dictionary state: activeDictionary, customDictionaries
 * - Config state: freeUserDailyQuota
 * - Language state: currentLanguage, translations, t()
 * - Persists auth state to localStorage
 *
 * @validates Requirements 8.7, 9.6, 4.1, 4.4
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/shallow';
import { createAuthSlice, loadAuthFromStorage } from './authSlice';
import { createSearchSlice } from './searchSlice';
import { createDictionarySlice } from './dictionarySlice';
import { createConfigSlice } from './configSlice';
import { createLanguageSlice } from './languageSlice';
import { createThemeSlice, initializeThemeOnLoad } from './themeSlice';
import type { StoreState } from './types';

// Initialize theme immediately to prevent flash
initializeThemeOnLoad();

/**
 * Main Zustand store
 *
 * Combines auth, search, and dictionary slices into a single store.
 * Auth state is hydrated from localStorage on initialization.
 *
 * Usage:
 * ```tsx
 * import { useStore } from '@/store';
 *
 * function MyComponent() {
 *   const user = useStore((state) => state.user);
 *   const login = useStore((state) => state.login);
 *   // ...
 * }
 * ```
 */
export const useStore = create<StoreState>()((...args) => {
  // Create all slices
  const authSlice = createAuthSlice(...args);
  const searchSlice = createSearchSlice(...args);
  const dictionarySlice = createDictionarySlice(...args);
  const configSlice = createConfigSlice(...args);
  const languageSlice = createLanguageSlice(...args);
  const themeSlice = createThemeSlice(...args);

  // Load persisted auth state from localStorage
  const persistedAuth = loadAuthFromStorage();

  return {
    // Combine all slices
    ...authSlice,
    ...searchSlice,
    ...dictionarySlice,
    ...configSlice,
    ...languageSlice,
    ...themeSlice,
    // Override with persisted values
    ...persistedAuth,
  };
});

// Re-export types for convenience
export type { StoreState } from './types';
export type {
  AuthSlice,
  SearchSlice,
  DictionarySlice,
  ConfigSlice,
  UserState,
  SearchState,
  DictionaryState,
  ConfigState,
} from './types';
export type { LanguageSlice } from './languageSlice';
export type { ThemeSlice } from './themeSlice';

/**
 * Selector hooks for common use cases
 * These provide better TypeScript inference and prevent unnecessary re-renders
 */

/**
 * Select only auth-related state
 */
export const useAuth = () =>
  useStore((state) => ({
    user: state.user,
    token: state.token,
    isAuthenticated: state.isAuthenticated,
    subscriptionType: state.subscriptionType,
    isLoading: state.isLoading,
    error: state.error,
  }));

/**
 * Select auth actions only
 */
export const useAuthActions = () =>
  useStore((state) => ({
    login: state.login,
    logout: state.logout,
    setUser: state.setUser,
    setToken: state.setToken,
    setSubscriptionType: state.setSubscriptionType,
    setLoading: state.setLoading,
    setError: state.setError,
    clearError: state.clearError,
    isPremium: state.isPremium,
    isAdmin: state.isAdmin,
  }));

/**
 * Select only search-related state
 */
export const useSearch = () =>
  useStore((state) => ({
    query: state.query,
    results: state.results,
    totalCount: state.totalCount,
    isLoading: state.isLoading,
    searchTimeMs: state.searchTimeMs,
    cached: state.cached,
    quota: state.quota,
    error: state.error,
  }));

/**
 * Select search actions only
 */
export const useSearchActions = () =>
  useStore((state) => ({
    setQuery: state.setQuery,
    setResults: state.setResults,
    clearResults: state.clearResults,
    setSearchLoading: state.setSearchLoading,
    setQuota: state.setQuota,
    decrementQuota: state.decrementQuota,
    setSearchError: state.setSearchError,
    clearSearchError: state.clearSearchError,
    resetSearch: state.resetSearch,
  }));

/**
 * Select only dictionary-related state
 */
export const useDictionary = () =>
  useStore((state) => ({
    activeDictionary: state.activeDictionary,
    availableDictionaries: state.availableDictionaries,
    customDictionaries: state.customDictionaries,
    isLoading: state.isLoading,
    error: state.error,
  }));

/**
 * Select dictionary actions only
 */
export const useDictionaryActions = () =>
  useStore((state) => ({
    setActiveDictionary: state.setActiveDictionary,
    setAvailableDictionaries: state.setAvailableDictionaries,
    setCustomDictionaries: state.setCustomDictionaries,
    addCustomDictionary: state.addCustomDictionary,
    removeCustomDictionary: state.removeCustomDictionary,
    setDictionaryLoading: state.setDictionaryLoading,
    setDictionaryError: state.setDictionaryError,
    clearDictionaryError: state.clearDictionaryError,
    resetToDefault: state.resetToDefault,
  }));

/**
 * Select only config-related state
 */
export const useConfig = () =>
  useStore(useShallow((state) => ({
    freeUserDailyQuota: state.freeUserDailyQuota,
    premiumMonthlyPrice: state.premiumMonthlyPrice,
    premiumSubscriptionPrice: state.premiumSubscriptionPrice,
    paypalClientId: state.paypalClientId,
    paypalPlanId: state.paypalPlanId,
    maintenanceMode: state.maintenanceMode,
    configLoaded: state.configLoaded,
  })));

/**
 * Select config actions only
 */
export const useConfigActions = () =>
  useStore((state) => ({
    setFreeUserDailyQuota: state.setFreeUserDailyQuota,
    setPremiumMonthlyPrice: state.setPremiumMonthlyPrice,
    setPremiumSubscriptionPrice: state.setPremiumSubscriptionPrice,
    setPaypalClientId: state.setPaypalClientId,
    setPaypalPlanId: state.setPaypalPlanId,
    setMaintenanceMode: state.setMaintenanceMode,
    loadPublicConfig: state.loadPublicConfig,
  }));

/**
 * Select only language-related state
 */
export const useLanguage = () =>
  useStore((state) => ({
    currentLanguage: state.currentLanguage,
    translations: state.translations,
    isLanguageLoading: state.isLanguageLoading,
  }));

/**
 * Select language actions only
 */
export const useLanguageActions = () =>
  useStore((state) => ({
    setLanguage: state.setLanguage,
    t: state.t,
    getLocale: state.getLocale,
    initializeLanguage: state.initializeLanguage,
  }));

/**
 * Select only theme-related state
 */
export const useTheme = () =>
  useStore((state) => ({
    darkMode: state.darkMode,
    isThemeSaving: state.isThemeSaving,
  }));

/**
 * Select theme actions only
 */
export const useThemeActions = () =>
  useStore((state) => ({
    setDarkMode: state.setDarkMode,
    toggleDarkMode: state.toggleDarkMode,
    loadThemeFromServer: state.loadThemeFromServer,
    saveThemeToServer: state.saveThemeToServer,
    initializeTheme: state.initializeTheme,
  }));
